/**
 * "Hey Buddy" — unified always-listening wake-word engine (foreground only).
 * Web/Preview: webkitSpeechRecognition (continuous + interim, auto-restart).
 * Native app: @capacitor-community/speech-recognition (partial results +
 * restart loop + silence debounce to synthesize "final" results).
 *
 * State machine:
 *   passive --(wake word + command in same utterance)--> onCommand(text)
 *   passive --(wake word alone)--> awake (9s window) --(next final)--> onCommand
 * suspend()/resume() let the app pause listening while Buddy records,
 * thinks or SPEAKS (so he never hears himself).
 */
import { Capacitor } from "@capacitor/core";

const WAKE_RE = /\b(?:hey|hei|ei|e\s?ai|oi|ola|alo|a)?[\s,]*(?:buddy|buddie|badi|bady|badyy|body|bodi|buddi|bud+y|bade|beleza\s+buddy)\b/i;

export const wakeWordSupported = () => {
  if (typeof window === "undefined") return false;
  if (Capacitor.isNativePlatform()) {
    // iOS-only on native. Android's SpeechRecognizer CANNOT run continuously:
    // it plays the system mic beep on every ~1.2s restart (beep-beep loop the
    // user hears) AND fights over the microphone with hold-to-talk recordings,
    // truncating them. On Android, Buddy is hold-to-talk only.
    return Capacitor.getPlatform() === "ios";
  }
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
};

/** Barge-in ("Hey Buddy" interrupts Buddy mid-speech) is only safe where the
 * OS does NOT duck app audio when the microphone turns on. Phones (native app
 * AND mobile browsers) lower/mute playback as soon as speech recognition
 * starts — Buddy's voice would fade out ~1s into every reply. Desktop web is
 * the only place we keep the mic hot while Buddy talks. */
export const bargeInCapable = () => {
  if (typeof window === "undefined") return false;
  if (Capacitor.isNativePlatform()) return false;
  return !/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
};

/** ---- GLOBAL MIC COORDINATION ----
 * ANY microphone user in the app (chat voice notes, Buddy hold-to-talk, video
 * recording, dictation...) MUST hold the mic exclusively: opening or closing
 * getUserMedia while the iOS SFSpeechRecognizer engine is running crashes the
 * whole app (AVAudioEngine session reconfiguration). mic.js acquires this
 * lock before getUserMedia; the wake listener pauses itself and only resumes
 * after the mic is fully released (with a settle delay). */
const _listeners = new Set();
let _micBusy = 0;

export function micExclusiveAcquire() {
  _micBusy += 1;
  let engineWasRunning = false;
  _listeners.forEach((l) => { if (l.__pauseForMic()) engineWasRunning = true; });
  return engineWasRunning;
}

export function micExclusiveRelease(settleMs = 350) {
  _micBusy = Math.max(0, _micBusy - 1);
  if (_micBusy > 0) return;
  // let the audio session settle before the recognizer restarts (iOS crash)
  setTimeout(() => {
    if (_micBusy === 0 && !_bgPaused) _listeners.forEach((l) => l.__resumeAfterMic());
  }, settleMs);
}

export const isMicBusy = () => _micBusy > 0;

/** ---- BACKGROUND / FOREGROUND ----
 * iOS interrupts the audio session the moment the app leaves the foreground.
 * If the SFSpeechRecognizer restart loop keeps firing while suspended, the
 * WebView returns to a dead audio engine and the UI freezes until the app is
 * killed. Pause every listener when the app hides; bring it back (with a
 * settle delay) once it is visible again. */
let _bgPaused = false;
function _onAppHidden() {
  if (_bgPaused) return;
  _bgPaused = true;
  _listeners.forEach((l) => { try { l.__pauseForMic(); } catch (e) { /* ignore */ } });
}
function _onAppVisible() {
  if (!_bgPaused) return;
  _bgPaused = false;
  setTimeout(() => {
    if (_bgPaused || _micBusy > 0) return;
    if (typeof document !== "undefined" && document.hidden) return;
    _listeners.forEach((l) => { try { l.__resumeAfterMic(); } catch (e) { /* ignore */ } });
  }, 900);
}
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => (document.hidden ? _onAppHidden() : _onAppVisible()));
  if (Capacitor.isNativePlatform()) {
    import("@capacitor/app").then(({ App }) => {
      App.addListener("appStateChange", ({ isActive }) => (isActive ? _onAppVisible() : _onAppHidden()));
    }).catch(() => {});
  }
}

/** ---- PLAYBACK FOCUS ----
 * Any audio/video PLAYBACK (voice notes, Buddy's voice, videos) must hold the
 * same lock: WebKit reconfigures the shared AVAudioSession the moment media
 * starts/stops, and with the SFSpeechRecognizer AVAudioEngine running that
 * reconfiguration kills the whole app on iOS ("plays 1s, then crash").
 * Acquire BEFORE play() (awaits the engine teardown on native), release when
 * playback ends/pauses — the recognizer comes back after a settle delay. */
const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export async function playbackFocusAcquire() {
  const engineWasRunning = micExclusiveAcquire();
  if (Capacitor.isNativePlatform()) await _sleep(engineWasRunning ? 400 : 60);
}

export function playbackFocusRelease() {
  micExclusiveRelease(Capacitor.isNativePlatform() ? 900 : 350);
}

export function createWakeListener({ lang = "en", onWake, onCommand, onState, onPrefix }) {
  let state = "off"; // off | passive | awake | denied
  let suspended = false;
  let stopping = false;
  let micPaused = false; // engine paused because another mic user holds the mic
  let webRec = null;
  let nativePlugin = null;
  let awakeTimer = null;
  let nativeDebounce = null;
  let lastPartial = "";
  let nativeKick = null; // (native) re-arms the listen restart loop after resume()
  let kickToken = 0;     // invalidates stale restart loops on suspend/stop
  const recLang = lang === "pt" ? "pt-BR" : "en-US";

  const setState = (s) => { state = s; try { onState?.(s); } catch (e) { /* ignore */ } };

  const handleTranscript = (raw, isFinal) => {
    if (suspended || state === "off" || !raw) return;
    const low = raw.toLowerCase();
    if (state === "passive") {
      const m = low.match(WAKE_RE);
      if (!m) return;
      const after = raw.slice(low.indexOf(m[0]) + m[0].length).replace(/^[\s,.!?]+/, "").trim();
      if (isFinal && after.split(/\s+/).filter(Boolean).length >= 1) {
        try { onCommand?.(after); } catch (e) { /* ignore */ }
        return;
      }
      setState("awake");
      // words already heard AFTER "Hey Buddy" — the recorder isn't live yet, so they would be lost
      try { onPrefix?.(after); } catch (e) { /* ignore */ }
      try { onWake?.(after); } catch (e) { /* ignore */ }
      clearTimeout(awakeTimer);
      awakeTimer = setTimeout(() => { if (state === "awake") setState("passive"); }, 9000);
    } else if (state === "awake" && !isFinal) {
      try { onPrefix?.(raw.replace(WAKE_RE, "").replace(/^[\s,.!?]+/, "").trim()); } catch (e) { /* ignore */ }
    } else if (state === "awake" && isFinal) {
      const cleaned = raw.replace(WAKE_RE, "").replace(/^[\s,.!?]+/, "").trim();
      if (cleaned.split(/\s+/).filter(Boolean).length >= 1) {
        clearTimeout(awakeTimer);
        setState("passive");
        try { onCommand?.(cleaned); } catch (e) { /* ignore */ }
      }
    }
  };

  // ---------- web engine ----------
  const startWeb = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return false;
    const rec = new SR();
    rec.lang = recLang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        handleTranscript(e.results[i][0]?.transcript || "", e.results[i].isFinal);
      }
    };
    rec.onend = () => {
      if (!stopping && !suspended && !micPaused && state !== "off") {
        setTimeout(() => { try { rec.start(); } catch (e) { /* already started */ } }, 250);
      }
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        stopping = true;
        setState("denied");
      }
    };
    try { if (!micPaused) rec.start(); } catch (e) { /* ignore */ }
    webRec = rec;
    return true;
  };

  // ---------- native engine (Capacitor) ----------
  const startNative = async () => {
    try {
      const mod = await import("@capacitor-community/speech-recognition");
      const SpeechRecognition = mod.SpeechRecognition;
      const avail = await SpeechRecognition.available();
      if (!avail?.available) return false;
      const perm = await SpeechRecognition.requestPermissions();
      if (perm?.speechRecognition && perm.speechRecognition !== "granted") {
        setState("denied");
        return false;
      }
      await SpeechRecognition.removeAllListeners();
      await SpeechRecognition.addListener("partialResults", (data) => {
        const t = (data?.matches && data.matches[0]) || "";
        if (!t) return;
        lastPartial = t;
        handleTranscript(t, false);
        // silence debounce: unchanged partial for 1.4s = treat as final
        clearTimeout(nativeDebounce);
        nativeDebounce = setTimeout(() => {
          if (lastPartial === t) handleTranscript(t, true);
        }, 1400);
      });
      const kick = async (tok) => {
        // NOTE: never check `state` here — at startup this runs BEFORE start()
        // sets state to "passive", and bailing out would kill wake listening
        if (tok !== kickToken || stopping || suspended || micPaused) return;
        try {
          await SpeechRecognition.start({ language: recLang, partialResults: true, popup: false });
        } catch (e) { /* ignore */ }
        if (tok === kickToken && !stopping && !suspended && !micPaused && state !== "off") {
          setTimeout(() => kick(tok), 1200); // restart loop: start() resolves when listening ends
        }
      };
      nativeKick = () => { kickToken += 1; kick(kickToken); };
      nativeKick();
      nativePlugin = SpeechRecognition;
      return true;
    } catch (e) {
      return false;
    }
  };

  const api = {
    start: async () => {
      stopping = false;
      suspended = false;
      micPaused = _micBusy > 0 || _bgPaused; // another mic user already holds the lock — engine starts on release
      const ok = Capacitor.isNativePlatform() ? await startNative() : startWeb();
      if (ok && state !== "denied") setState("passive");
      if (ok) _listeners.add(api);
      return ok;
    },
    stop: async () => {
      stopping = true;
      kickToken += 1; // kill any pending native restart loop
      clearTimeout(awakeTimer);
      clearTimeout(nativeDebounce);
      setState("off");
      _listeners.delete(api);
      try { webRec?.stop(); } catch (e) { /* ignore */ }
      try { await nativePlugin?.stop(); } catch (e) { /* ignore */ }
    },
    suspend: () => {
      if (suspended) return;
      suspended = true;
      kickToken += 1; // kill any pending native restart loop
      clearTimeout(awakeTimer);
      try { webRec?.stop(); } catch (e) { /* ignore */ }
      try { nativePlugin?.stop(); } catch (e) { /* ignore */ }
    },
    resume: () => {
      if (!suspended) return;
      suspended = false;
      if (state === "off" || state === "denied") return;
      setState("passive");
      if (micPaused) return; // another mic user holds the mic — engine starts on release
      if (Capacitor.isNativePlatform()) {
        // re-arm the restart loop — a one-shot start() would die after the
        // first listen session and "Hey Buddy" would silently stop working.
        // Small delay: the mic stream released just before resume() may still
        // be tearing its audio session down — starting SFSpeechRecognizer in
        // the middle of that reconfiguration can crash the whole app on iOS.
        setTimeout(() => {
          if (suspended || stopping || micPaused || state === "off") return;
          if (nativeKick) nativeKick();
          else {
            try {
              nativePlugin?.start({ language: recLang, partialResults: true, popup: false });
            } catch (e) { /* ignore */ }
          }
        }, 280);
      } else {
        try { webRec?.start(); } catch (e) { /* ignore */ }
      }
    },
    getState: () => state,
    /** (internal) another app feature is opening the microphone — stop the
     * recognition engine WITHOUT touching the owner's suspend intent.
     * Returns true when the engine was actually running. */
    __pauseForMic: () => {
      const wasRunning = !micPaused && !suspended && !stopping && state !== "off" && state !== "denied";
      if (micPaused) return false;
      micPaused = true;
      kickToken += 1;
      clearTimeout(awakeTimer);
      try { webRec?.stop(); } catch (e) { /* ignore */ }
      try { nativePlugin?.stop(); } catch (e) { /* ignore */ }
      return wasRunning;
    },
    /** (internal) the mic is free again — restart the engine if the owner
     * still wants it on. Called with a settle delay by micExclusiveRelease. */
    __resumeAfterMic: () => {
      if (!micPaused) return;
      micPaused = false;
      if (suspended || stopping || state === "off" || state === "denied") return;
      if (Capacitor.isNativePlatform()) {
        if (nativeKick) nativeKick();
      } else {
        try { webRec?.start(); } catch (e) { /* ignore */ }
      }
    },
  };
  return api;
}
